'use client'

import { useEffect, useState, useRef, useCallback, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import DailyIframe, {
    DailyCall,
    DailyEventObjectParticipant,
    DailyEventObjectParticipantLeft,
} from '@daily-co/daily-js'
import {
    MdVideocam,
    MdVideocamOff,
    MdMic,
    MdMicOff,
    MdScreenShare,
    MdStopScreenShare,
    MdCallEnd,
    MdChat,
    MdSend,
    MdClose,
    MdFiberManualRecord,
    MdArrowBack,
    MdFullscreen,
    MdFullscreenExit,
    MdContentCopy,
    MdEmail,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { SocketContext } from '@/lib/providers/socket-provider'
import {
    getTeleconsultation,
    startTeleconsultation,
    rejoinTeleconsultation,
    endTeleconsultation,
    resendTeleconsultationLink,
    sendTeleconsultationChat,
    getTeleconsultationChat,
    TeleconsultationRoom,
    TeleconsultationChatMessage,
    TELECONSULTATION_STATUS_LABELS,
} from '@/lib/api/teleconsultation/teleconsultation-api'
import ConfirmModal from '@/components/confirm-modal'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function TeleconsultationRoomPage() {
    const { user } = useAuth()
    const { socket, isConnected } = useContext(SocketContext)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()

    const [teleconsultation, setTeleconsultation] =
        useState<TeleconsultationRoom | null>(null)
    const [loading, setLoading] = useState(true)
    const [chatMessages, setChatMessages] = useState<
        TeleconsultationChatMessage[]
    >([])
    const [chatMessage, setChatMessage] = useState('')
    const [showChat, setShowChat] = useState(false)
    const [sendingMessage, setSendingMessage] = useState(false)
    const [showEndModal, setShowEndModal] = useState(false)
    const [unreadCount, setUnreadCount] = useState(0)
    const [resending, setResending] = useState(false)

    const [isCameraOn, setIsCameraOn] = useState(true)
    const [isMicOn, setIsMicOn] = useState(true)
    const [isScreenSharing, setIsScreenSharing] = useState(false)
    const [isRecording, setIsRecording] = useState(false)
    const [elapsedTime, setElapsedTime] = useState(0)
    const [patientConnected, setPatientConnected] = useState(false)
    const [roomStatus, setRoomStatus] = useState<string>('')
    const [isFullscreen, setIsFullscreen] = useState(false)

    const roomContainerRef = useRef<HTMLDivElement>(null)
    const showChatRef = useRef(false)
    const callObjectRef = useRef<DailyCall | null>(null)
    const remoteVideoRef = useRef<HTMLVideoElement>(null)
    const localVideoRef = useRef<HTMLVideoElement>(null)
    const chatEndRef = useRef<HTMLDivElement>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (id) {
            loadTeleconsultation()
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
            destroyDaily()
        }
    }, [id])

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [chatMessages])

    // Sincronizar ref do chat e zerar contador ao abrir
    useEffect(() => {
        showChatRef.current = showChat
        if (showChat) {
            setUnreadCount(0)
        }
    }, [showChat])

    // Socket: escutar eventos em tempo real
    useEffect(() => {
        if (!socket || !isConnected || !teleconsultation) return

        const tcId = teleconsultation.id

        // Entrar na room do socket
        socket.emit('teleconsultation:join', { teleconsultationId: tcId })

        const handleChatMessage = (data: {
            teleconsultationId: number
            message: TeleconsultationChatMessage
        }) => {
            if (data.teleconsultationId === tcId) {
                setChatMessages(prev => {
                    const exists = prev.some(m => m.id === data.message.id)
                    if (exists) return prev
                    return [...prev, data.message]
                })
                // Se o chat está fechado e a mensagem é do paciente
                if (
                    !showChatRef.current &&
                    data.message.senderRole !== 'PROFESSIONAL'
                ) {
                    setUnreadCount(prev => prev + 1)
                    toast.info(
                        `${data.message.senderName}: ${data.message.message}`,
                        { autoClose: 4000 }
                    )
                }
            }
        }

        const handleStatusChanged = (data: {
            teleconsultationId: number
            status: string
        }) => {
            if (data.teleconsultationId === tcId) {
                setRoomStatus(data.status)
                if (data.status === 'IN_PROGRESS') {
                    setPatientConnected(true)
                }
                if (data.status === 'COMPLETED') {
                    if (timerRef.current) clearInterval(timerRef.current)
                    destroyDaily()
                    toast.info('Teleconsulta encerrada')
                    navigate(`/bookings/teleconsultations/${tcId}`)
                }
            }
        }

        const handleParticipantJoined = (data: {
            teleconsultationId: number
            role: string
            name: string
        }) => {
            if (data.teleconsultationId === tcId && data.role === 'PATIENT') {
                setPatientConnected(true)
                toast.info(`${data.name} entrou na sala`)
            }
        }

        const handleParticipantLeft = (data: {
            teleconsultationId: number
            role: string
            name: string
        }) => {
            if (data.teleconsultationId === tcId && data.role === 'PATIENT') {
                setPatientConnected(false)
                toast.warning(`${data.name} saiu da sala`)
            }
        }

        socket.on('teleconsultation:chat-message', handleChatMessage)
        socket.on('teleconsultation:status-changed', handleStatusChanged)
        socket.on(
            'teleconsultation:participant-joined',
            handleParticipantJoined
        )
        socket.on('teleconsultation:participant-left', handleParticipantLeft)

        return () => {
            socket.emit('teleconsultation:leave', { teleconsultationId: tcId })
            socket.off('teleconsultation:chat-message', handleChatMessage)
            socket.off('teleconsultation:status-changed', handleStatusChanged)
            socket.off(
                'teleconsultation:participant-joined',
                handleParticipantJoined
            )
            socket.off(
                'teleconsultation:participant-left',
                handleParticipantLeft
            )
        }
    }, [socket, isConnected, teleconsultation?.id])

    const loadTeleconsultation = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getTeleconsultation(parseInt(id, 10))
            if (response.success && response.result) {
                setTeleconsultation(response.result)
                setRoomStatus(response.result.status)

                // Se ainda não foi iniciada, iniciar automaticamente
                if (
                    ['CREATED', 'WAITING_DOCTOR'].includes(
                        response.result.status
                    )
                ) {
                    const startResp = await startTeleconsultation(
                        parseInt(id, 10)
                    )
                    if (startResp.success) {
                        setTeleconsultation(startResp.result)
                        setRoomStatus(startResp.result.status)
                        startTimer(startResp.result.startedAt)
                        await initializeDaily(startResp.result)
                    }
                } else if (
                    ['WAITING_PATIENT', 'IN_PROGRESS'].includes(
                        response.result.status
                    )
                ) {
                    // Renovar sala e tokens antes de entrar (podem ter expirado)
                    const rejoinResp = await rejoinTeleconsultation(
                        parseInt(id, 10)
                    )
                    if (rejoinResp.success) {
                        setTeleconsultation(rejoinResp.result)
                        startTimer(rejoinResp.result.startedAt)
                        await initializeDaily(rejoinResp.result)
                    } else {
                        startTimer(response.result.startedAt)
                        await initializeDaily(response.result)
                    }
                }

                await loadChat()
            } else {
                toast.error(
                    response.error || 'Erro ao carregar teleconsulta'
                )
                navigate('/bookings/teleconsultations')
            }
        } catch {
            toast.error('Erro ao carregar teleconsulta')
            navigate('/bookings/teleconsultations')
        } finally {
            setLoading(false)
        }
    }

    const initializeDaily = async (tc: TeleconsultationRoom) => {
        if (callObjectRef.current) return
        if (!tc.dailyRoomUrl || !tc.professionalDailyToken) {
            toast.error('Dados da sala de vídeo indisponíveis')
            return
        }

        try {
            const callObject = DailyIframe.createCallObject({
                audioSource: true,
                videoSource: true,
            })

            callObjectRef.current = callObject

            // Eventos do Daily.co
            callObject.on(
                'participant-joined',
                (event?: DailyEventObjectParticipant) => {
                    if (event?.participant && !event.participant.local) {
                        setPatientConnected(true)
                        attachRemoteVideo(event.participant)
                    }
                }
            )

            callObject.on(
                'participant-updated',
                (event?: DailyEventObjectParticipant) => {
                    if (event?.participant && !event.participant.local) {
                        attachRemoteVideo(event.participant)
                    }
                    if (event?.participant?.local) {
                        attachLocalVideo(event.participant)
                    }
                }
            )

            callObject.on(
                'participant-left',
                (event?: DailyEventObjectParticipantLeft) => {
                    if (event?.participant && !event.participant.local) {
                        setPatientConnected(false)
                        if (remoteVideoRef.current) {
                            remoteVideoRef.current.srcObject = null
                        }
                    }
                }
            )

            callObject.on('error', (event) => {
                console.error('[Daily] Error:', event)
                toast.error('Erro na conexão de vídeo')
            })

            // Entrar na sala
            await callObject.join({
                url: tc.dailyRoomUrl,
                token: tc.professionalDailyToken,
            })

            // Attach local video
            const localParticipant = callObject.participants().local
            if (localParticipant) {
                attachLocalVideo(localParticipant)
            }

            // Check existing remote participants
            const participants = callObject.participants()
            for (const [key, participant] of Object.entries(participants)) {
                if (key !== 'local' && participant) {
                    setPatientConnected(true)
                    attachRemoteVideo(participant)
                }
            }
        } catch (error: any) {
            console.error('[Daily] Init error:', error)
            toast.error('Erro ao inicializar vídeo:' + error.message)
        }
    }

    const attachRemoteVideo = (participant: any) => {
        if (!remoteVideoRef.current) return
        const videoTrack = participant.tracks?.video
        if (videoTrack?.track) {
            const stream = new MediaStream([videoTrack.track])
            remoteVideoRef.current.srcObject = stream
        }
    }

    const attachLocalVideo = (participant: any) => {
        if (!localVideoRef.current) return
        const videoTrack = participant.tracks?.video
        if (videoTrack?.track) {
            const stream = new MediaStream([videoTrack.track])
            localVideoRef.current.srcObject = stream
        }
    }

    const destroyDaily = useCallback(() => {
        if (callObjectRef.current) {
            callObjectRef.current.leave().catch(() => { })
            callObjectRef.current.destroy().catch(() => { })
            callObjectRef.current = null
        }
    }, [])

    const startTimer = (startedAt?: string) => {
        if (timerRef.current) clearInterval(timerRef.current)

        const start = startedAt ? new Date(startedAt).getTime() : Date.now()
        timerRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - start) / 1000)
            setElapsedTime(elapsed)
        }, 1000)
    }

    const formatElapsedTime = (seconds: number) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor((seconds % 3600) / 60)
        const s = seconds % 60
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
        }
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    }

    // Fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange
            )
        }
    }, [])

    const toggleFullscreen = async () => {
        if (!roomContainerRef.current) return
        if (!document.fullscreenElement) {
            await roomContainerRef.current.requestFullscreen()
        } else {
            await document.exitFullscreen()
        }
    }

    const copyAccessLink = () => {
        const link = teleconsultation?.accessLink
        if (!link) {
            toast.warning('Link de acesso não disponível')
            return
        }
        navigator.clipboard.writeText(link)
        toast.success('Link copiado para a área de transferência')
    }

    const handleResendLink = async () => {
        if (!id || resending) return
        setResending(true)
        try {
            const response = await resendTeleconsultationLink(
                parseInt(id, 10)
            )
            if (response.success) {
                toast.success('Link reenviado por email ao paciente')
            } else {
                toast.error(response.error || 'Erro ao reenviar link')
            }
        } catch {
            toast.error('Erro ao reenviar link')
        } finally {
            setResending(false)
        }
    }

    const loadChat = async () => {
        if (!id) return
        try {
            const response = await getTeleconsultationChat(parseInt(id, 10))
            if (response.success) {
                setChatMessages(response.result || [])
            }
        } catch {
            // Silencioso
        }
    }

    const handleSendMessage = async () => {
        if (!id || !chatMessage.trim()) return

        setSendingMessage(true)
        try {
            const response = await sendTeleconsultationChat(
                parseInt(id, 10),
                chatMessage.trim()
            )
            if (response.success) {
                setChatMessage('')
            } else {
                toast.error('Erro ao enviar mensagem')
            }
        } catch {
            toast.error('Erro ao enviar mensagem')
        } finally {
            setSendingMessage(false)
        }
    }

    const handleEnd = async () => {
        if (!id) return

        try {
            destroyDaily()
            const response = await endTeleconsultation(parseInt(id, 10))
            if (response.success) {
                if (timerRef.current) clearInterval(timerRef.current)

                navigate(`/bookings/teleconsultations/${id}`)
            } else {
                toast.error(
                    response.error || 'Erro ao encerrar teleconsulta'
                )
            }
        } catch {
            toast.error('Erro ao encerrar teleconsulta')
        } finally {
            setShowEndModal(false)
        }
    }

    const toggleCamera = () => {
        const call = callObjectRef.current
        if (!call) return
        call.setLocalVideo(!isCameraOn)
        setIsCameraOn(prev => !prev)
    }

    const toggleMic = () => {
        const call = callObjectRef.current
        if (!call) return
        call.setLocalAudio(!isMicOn)
        setIsMicOn(prev => !prev)
    }

    const toggleScreenShare = async () => {
        const call = callObjectRef.current
        if (!call) return
        if (isScreenSharing) {
            call.stopScreenShare()
        } else {
            await call.startScreenShare()
        }
        setIsScreenSharing(prev => !prev)
    }

    const toggleRecording = async () => {
        const call = callObjectRef.current
        if (!call) return
        if (isRecording) {
            call.stopRecording()
        } else {
            await call.startRecording()
        }
        setIsRecording(prev => !prev)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60">
                    Preparando teleconsulta...
                </p>
            </div>
        )
    }

    if (!teleconsultation) return null

    const tc = teleconsultation
    const currentStatus = roomStatus || tc.status
    const isCompleted = ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(
        currentStatus
    )

    if (isCompleted) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-16 h-16 rounded-full bg-base-300 flex items-center justify-center">
                    <MdVideocamOff className="text-3xl text-base-content/60" />
                </div>
                <h2 className="text-xl font-bold">Teleconsulta Encerrada</h2>
                <p className="text-base-content/60">
                    Status:{' '}
                    {TELECONSULTATION_STATUS_LABELS[
                        currentStatus as keyof typeof TELECONSULTATION_STATUS_LABELS
                    ] || currentStatus}
                </p>
                <button
                    onClick={() => navigate(`/bookings/teleconsultations/${tc.id}`)}
                    className="btn btn-secondary gap-2"
                >
                    <MdArrowBack />
                    Ver Detalhes
                </button>
            </div>
        )
    }

    return (
        <div
            ref={roomContainerRef}
            className={`flex bg-base-300 overflow-hidden ${isFullscreen ? 'h-screen' : 'h-[calc(100vh-5rem)]'
                }`}
        >
            {/* Área principal de vídeo */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-base-100 border-b border-base-300 px-4 py-3 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(`/bookings/teleconsultations/${tc.id}`)}
                            className="btn btn-ghost btn-sm btn-circle"
                        >
                            <MdArrowBack />
                        </button>
                        <div>
                            <h2 className="font-semibold text-sm">
                                {tc.client?.name || 'Paciente'}
                            </h2>
                            <p className="text-xs text-base-content/60">
                                {patientConnected
                                    ? 'Conectado'
                                    : 'Aguardando paciente...'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="tooltip tooltip-bottom" data-tip="Copiar link do paciente">
                            <button
                                onClick={copyAccessLink}
                                className="btn btn-ghost btn-sm btn-circle"
                            >
                                <MdContentCopy className="text-base" />
                            </button>
                        </div>
                        <div className="tooltip tooltip-bottom" data-tip="Reenviar link por email">
                            <button
                                onClick={handleResendLink}
                                disabled={resending}
                                className="btn btn-ghost btn-sm btn-circle"
                            >
                                {resending ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                    <MdEmail className="text-base" />
                                )}
                            </button>
                        </div>
                        <div className="w-px h-6 bg-base-300"></div>
                        <div className="font-mono text-sm bg-base-200 px-3 py-1 rounded-lg">
                            {formatElapsedTime(elapsedTime)}
                        </div>
                        {isRecording && (
                            <div className="flex items-center gap-1 text-error text-xs">
                                <MdFiberManualRecord className="animate-pulse" />
                                Gravando
                            </div>
                        )}
                    </div>
                </div>

                {/* Área de vídeo */}
                <div className="flex-1 min-h-0 relative bg-base-300 flex items-center justify-center overflow-hidden">
                    {/* Vídeo remoto (paciente) */}
                    <div className="w-full h-full flex items-center justify-center">
                        {!patientConnected ? (
                            <div className="text-center">
                                <div className="loading loading-dots loading-lg text-secondary mb-4"></div>
                                <h3 className="text-lg font-semibold mb-2">
                                    Aguardando paciente...
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    O paciente receberá o link de acesso por
                                    email.
                                </p>
                            </div>
                        ) : (
                            <video
                                ref={remoteVideoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover bg-neutral rounded-lg"
                            />
                        )}
                    </div>

                    {/* Vídeo local (profissional) - miniatura */}
                    <div className="absolute bottom-4 right-4 w-48 h-36 bg-neutral rounded-lg shadow-xl border-2 border-base-100 overflow-hidden">
                        {isCameraOn ? (
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-neutral-content/50">
                                <MdVideocamOff className="text-2xl" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Controles */}
                <div className="bg-base-100 border-t border-base-300 px-4 py-4 flex items-center justify-center gap-3 shrink-0">
                    <button
                        onClick={toggleMic}
                        className={`btn btn-circle ${isMicOn ? 'btn-ghost' : 'btn-error'
                            }`}
                        title={
                            isMicOn
                                ? 'Desativar microfone'
                                : 'Ativar microfone'
                        }
                    >
                        {isMicOn ? (
                            <MdMic className="text-xl" />
                        ) : (
                            <MdMicOff className="text-xl" />
                        )}
                    </button>

                    <button
                        onClick={toggleCamera}
                        className={`btn btn-circle ${isCameraOn ? 'btn-ghost' : 'btn-error'
                            }`}
                        title={
                            isCameraOn
                                ? 'Desativar câmera'
                                : 'Ativar câmera'
                        }
                    >
                        {isCameraOn ? (
                            <MdVideocam className="text-xl" />
                        ) : (
                            <MdVideocamOff className="text-xl" />
                        )}
                    </button>

                    {tc.enableScreenShare && (
                        <button
                            onClick={toggleScreenShare}
                            className={`btn btn-circle ${isScreenSharing ? 'btn-secondary' : 'btn-ghost'
                                }`}
                            title="Compartilhar tela"
                        >
                            {isScreenSharing ? (
                                <MdStopScreenShare className="text-xl" />
                            ) : (
                                <MdScreenShare className="text-xl" />
                            )}
                        </button>
                    )}

                    {tc.recordingEnabled && (
                        <button
                            onClick={toggleRecording}
                            className={`btn btn-circle ${isRecording ? 'btn-error' : 'btn-ghost'
                                }`}
                            title={
                                isRecording
                                    ? 'Parar gravação'
                                    : 'Iniciar gravação'
                            }
                        >
                            <MdFiberManualRecord
                                className={`text-xl ${isRecording ? 'animate-pulse' : ''
                                    }`}
                            />
                        </button>
                    )}

                    {tc.enableChat && (
                        <div className="relative">
                            <button
                                onClick={() => setShowChat(prev => !prev)}
                                className={`btn btn-circle ${showChat ? 'btn-secondary' : 'btn-ghost'
                                    }`}
                                title="Chat"
                            >
                                <MdChat className="text-xl" />
                            </button>
                            {unreadCount > 0 && !showChat && (
                                <span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center rounded-full bg-error text-error-content text-xs font-bold px-1">
                                    {unreadCount > 99
                                        ? '99+'
                                        : unreadCount}
                                </span>
                            )}
                        </div>
                    )}

                    <button
                        onClick={toggleFullscreen}
                        className="btn btn-circle btn-ghost"
                        title={
                            isFullscreen
                                ? 'Sair da tela cheia'
                                : 'Tela cheia'
                        }
                    >
                        {isFullscreen ? (
                            <MdFullscreenExit className="text-xl" />
                        ) : (
                            <MdFullscreen className="text-xl" />
                        )}
                    </button>

                    <div className="w-px h-8 bg-base-300 mx-2"></div>

                    <button
                        onClick={() => setShowEndModal(true)}
                        className="btn btn-error btn-circle"
                        title="Encerrar teleconsulta"
                    >
                        <MdCallEnd className="text-xl" />
                    </button>
                </div>
            </div>

            {/* Sidebar de chat */}
            {showChat && tc.enableChat && (
                <div className="w-80 bg-base-100 border-l border-base-300 flex flex-col">
                    <div className="px-4 py-3 border-b border-base-300 flex items-center justify-between">
                        <h3 className="font-semibold text-sm">Chat</h3>
                        <button
                            onClick={() => setShowChat(false)}
                            className="btn btn-ghost btn-xs btn-circle"
                        >
                            <MdClose />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {chatMessages.length === 0 && (
                            <p className="text-center text-sm text-base-content/50 py-8">
                                Nenhuma mensagem ainda
                            </p>
                        )}
                        {chatMessages.map(msg => (
                            <div
                                key={msg.id}
                                className={`flex flex-col ${msg.senderRole === 'PROFESSIONAL'
                                    ? 'items-end'
                                    : 'items-start'
                                    }`}
                            >
                                <div
                                    className={`max-w-[90%] rounded-lg p-2 ${msg.senderRole === 'PROFESSIONAL'
                                        ? 'bg-secondary text-primary-content'
                                        : 'bg-base-200'
                                        }`}
                                >
                                    <p className="text-xs font-semibold mb-0.5">
                                        {msg.senderName}
                                    </p>
                                    <p className="text-sm">{msg.message}</p>
                                </div>
                                <p className="text-xs text-base-content/40 mt-0.5 px-1">
                                    {format(
                                        new Date(msg.createdAt),
                                        'HH:mm',
                                        { locale: ptBR }
                                    )}
                                </p>
                            </div>
                        ))}
                        <div ref={chatEndRef} />
                    </div>

                    <div className="p-3 border-t border-base-300">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className="input input-bordered input-sm flex-1"
                                placeholder="Digite uma mensagem..."
                                value={chatMessage}
                                onChange={e => setChatMessage(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault()
                                        handleSendMessage()
                                    }
                                }}
                                disabled={sendingMessage}
                            />
                            <button
                                onClick={handleSendMessage}
                                className="btn btn-secondary btn-sm btn-circle"
                                disabled={
                                    sendingMessage || !chatMessage.trim()
                                }
                            >
                                <MdSend />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showEndModal}
                onClose={() => setShowEndModal(false)}
                onConfirm={handleEnd}
                title="Encerrar teleconsulta"
                message="Deseja realmente encerrar esta teleconsulta? A chamada será finalizada para todos os participantes."
                confirmButtonText="Encerrar"
            />
        </div>
    )
}
